import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { of, switchMap } from 'rxjs';
import { PAGE, PortalPageEnum, SearchItems } from '../../core';
import { ListService, PatientService, TlhexService, UserService } from '../../services';
import { BaseComponent, ConfirmationComponent } from '../../shared';


/**
* Patient actions component on patient table.
*/
@Component({
	selector: 'patient-action',
	templateUrl: './patient-action.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PatientActionComponent extends BaseComponent {

	private readonly router = inject(Router);
	private readonly modalSrv = inject(NgbModal);
	private readonly listSrv = inject(ListService);
	private readonly userSrv = inject(UserService);
	private readonly patSrv = inject(PatientService);
	private readonly tlhexSrv = inject(TlhexService);


	item = input<SearchItems | null>(null);
	hasPublishedRevoked = computed(() => !!this.item()?.patientHasPublished || !!this.item()?.patientHasRevoked);
	isReadonly = toSignal(
		toObservable(this.item).pipe(
			switchMap(pat => pat ? this.userSrv.isCustomerCareOtherUser(pat.userId) : of(true))
		)
	);


	/**
	* Navigate to patient edit page.
	*/
	edit(ev: Event): void {
		ev.stopPropagation();
		if (!this.isReadonly()) {
			this.router.navigate([`/${PAGE.PATIENT_EDIT}`], { queryParams: { patientGuid: this.item()!.patientId } });
		}
	}

	/**
	* Navigate to plan creation page.
	*/
	createPlan(ev: Event): void {
		ev.stopPropagation();
		if (!this.isReadonly()) {
			this.router.navigate([`/${PAGE.PLAN_NEW}`], { queryParams: { patientGuid: this.item()!.patientId } });
		}
	}

	/**
	* Open confirmation modal before delete patient.
	*/
	openDeleteModal(ev: Event): void {
		ev.stopPropagation();
		if (!this.isReadonly() && !this.modalSrv.hasOpenModals()) {
			const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
			(modalRef.componentInstance as ConfirmationComponent).config = {
				title: this.labels().PATIENT_ACTION_COMPONENT_DELETE_MODAL_TITLE,
				message: this.labels().PATIENT_ACTION_COMPONENT_DELETE_MODAL_MESSAGE,
				cancelBtn: this.labels().PATIENT_ACTION_COMPONENT_DELETE_MODAL_NO,
				confirmBtn: this.labels().PATIENT_ACTION_COMPONENT_DELETE_MODAL_YES
			};
			modalRef.result.then(() => this.delete()).catch(error => false);
		}
	}

	private delete(): void {
		this.patSrv.deletePatient(this.item()!.patientId).subscribe(() => this.listSrv.search());
	}

	/**
	 * Navigate to myHexPlan patient settings.
	 */
	viewPatientSettings(ev: Event): void {
		ev.stopPropagation();
		if (!this.isReadonly()) {
			this.tlhexSrv.navigateToMyhexplan(PortalPageEnum.PatientSettings, this.item()!.patientId);
		}
	}

	/**
	 * Navigate to myhexPlan patient history.
	 */
	viewPatientHistory(ev: Event): void {
		ev.stopPropagation();
		if (!this.isReadonly()) {
			this.tlhexSrv.navigateToMyhexplan(PortalPageEnum.PatientDiary, this.item()!.patientId);
		}
	}
}
